import { lazy, Suspense, useState, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWatchlist } from "@/hooks/useWatchlist";
import { useLeaderboard, type LeaderboardEntry } from "@/hooks/useLeaderboard";
import { useUserRole } from "@/hooks/useUserRole";
import { SnapTradePositionsTab, SnapTradeOrdersTab } from "@/components/admin/SnapTradeDataTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Settings, TrendingUp, Eye, Users, Link2, ArrowRight, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Info, CheckCircle2, Crosshair, ClipboardList } from "lucide-react";
import InfluencerCard from "@/components/InfluencerCard";
import FollowSubscriptionModal from "@/components/FollowSubscriptionModal";
import { toast } from "sonner";
import { format } from "date-fns";
import { LeaderboardFilterRow, useLeaderboardFilters, filterInfluencer } from "@/components/LeaderboardFilters";
import { useGlobalInfluencerFilter } from "@/contexts/GlobalInfluencerFilterContext";
import ActiveFilterBadge from "@/components/ActiveFilterBadge";


/* ─── Brokerage CTA ─── */

const BrokerageCTA = ({ connected, onConnect }: { connected: boolean; onConnect: () => void }) => {
  return (
    <Card className="border-dashed border-primary/30">
      <CardContent className="flex flex-col sm:flex-row items-center gap-4 py-5">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-2.5">
            <Link2 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-semibold text-sm">
              {connected ? "Add another brokerage" : "Connect your brokerage"}
            </p>
            <p className="text-xs text-muted-foreground">
              {connected
                ? "Link additional brokerage accounts to track all your trades."
                : "Get real positions & trade history instead of X-based estimates."}
            </p>
          </div>
        </div>
        <Button size="sm" onClick={onConnect} className="ml-auto shrink-0">
          {connected ? "Add" : "Connect"} <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};

/* ─── Positions Tab ─── */

const PositionsTab = ({ userId }: { userId: string }) => {
  const navigate = useNavigate();

  // Find user's snaptrade mapping
  const { data: snapInfo } = useQuery({
    queryKey: ["dashboard-snap-info", userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("snaptrade_users")
        .select("x_user_id, snaptrade_user_id")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const snaptradeUserIds = useMemo(
    () => [...new Set((snapInfo ?? []).map((s) => s.snaptrade_user_id).filter(Boolean))] as string[],
    [snapInfo]
  );

  const xUserId = snapInfo?.[0]?.x_user_id ?? null;

  // Connected brokerage accounts
  const { data: connections = [] } = useQuery({
    queryKey: ["dashboard-brokerage-connections", snaptradeUserIds],
    enabled: snaptradeUserIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("brokerage_connections")
        .select("*")
        .in("snaptrade_user_id", snaptradeUserIds)
        .eq("status", "active")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  // Brokerage trades for the user
  const { data: brokerageTrades = [], isLoading: loadingTrades } = useQuery({
    queryKey: ["dashboard-brokerage-trades", userId],
    queryFn: async () => {
      if (!xUserId) return [];

      const { data, error } = await supabase
        .from("brokerage_trades")
        .select("*")
        .eq("x_user_id", xUserId)
        .order("trade_date", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!xUserId,
  });

  const hasBrokerage = (snapInfo ?? []).length > 0;

  return (
    <div className="space-y-4">
      {/* Connected brokerages */}
      {connections.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Connected Brokerages
            </p>
            <div className="space-y-2">
              {connections.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/40"
                >
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {(c as any).brokerage_name || "Brokerage"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Connected {format(new Date(c.created_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs bg-primary/10 text-primary">
                    Active
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* If connected but no brokerage_connections rows, show a simpler indicator */}
      {hasBrokerage && connections.length === 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/40">
              <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Brokerage linked</p>
                <p className="text-xs text-muted-foreground">
                  Your SnapTrade account is connected. Trades will appear once synced.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <BrokerageCTA
        connected={!!hasBrokerage}
        onConnect={() => navigate("/onboard/brokerage")}
      />

      {loadingTrades ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading trades…</div>
      ) : brokerageTrades.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <TrendingUp className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {hasBrokerage
                ? "No trades synced yet. Your brokerage data will appear here once synced."
                : "Connect your brokerage to see your real positions and trade history."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-xs">
                    <th className="text-left px-4 py-3 font-medium">Symbol</th>
                    <th className="text-left px-4 py-3 font-medium">Action</th>
                    <th className="text-right px-4 py-3 font-medium">Qty</th>
                    <th className="text-right px-4 py-3 font-medium">Price</th>
                    <th className="text-right px-4 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {brokerageTrades.map((t) => (
                    <tr key={t.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-mono font-medium">{t.symbol}</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="secondary"
                          className={
                            t.action.toLowerCase().includes("buy")
                              ? "bg-profit/10 text-profit"
                              : "bg-loss/10 text-loss"
                          }
                        >
                          {t.action}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{t.quantity ?? "—"}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        {t.price != null ? `$${Number(t.price).toFixed(2)}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {t.trade_date ? format(new Date(t.trade_date), "MMM d, yyyy") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ─── Watchlist Tab ─── */

const WatchlistTab = () => {
  const { watchlist, isLoading } = useWatchlist();
  const navigate = useNavigate();

  // Fetch asset info + prices for watched tickers
  const tickers = watchlist.map((w) => w.ticker);

  const { data: assetInfo = [] } = useQuery({
    queryKey: ["dashboard-watchlist-info", tickers],
    enabled: tickers.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("asset_info")
        .select("ticker, company_name, logo_url")
        .in("ticker", tickers);
      return data ?? [];
    },
  });

  const { data: assetPrices = [] } = useQuery({
    queryKey: ["dashboard-watchlist-prices", tickers],
    enabled: tickers.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("asset_prices")
        .select("ticker, current_price")
        .in("ticker", tickers);
      return data ?? [];
    },
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading watchlist…</div>;
  }

  if (watchlist.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Eye className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            No assets on your watchlist yet. Browse the leaderboard and add tickers to watch.
          </p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate("/leaderboard")}>
            Browse leaderboard
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-xs">
                <th className="text-left px-4 py-3 font-medium">Ticker</th>
                <th className="text-left px-4 py-3 font-medium">Company</th>
                <th className="text-right px-4 py-3 font-medium">Price</th>
                <th className="text-right px-4 py-3 font-medium">Added</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.map((w) => {
                const info = assetInfo.find((a) => a.ticker === w.ticker);
                const price = assetPrices.find((p) => p.ticker === w.ticker);
                return (
                  <tr
                    key={w.id}
                    className="border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/asset/${w.ticker}`)}
                  >
                    <td className="px-4 py-3 font-mono font-medium">{w.ticker}</td>
                    <td className="px-4 py-3 text-muted-foreground">{info?.company_name ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {price?.current_price != null ? `$${Number(price.current_price).toFixed(2)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">
                      {format(new Date(w.created_at), "MMM d")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── Follows Tab (Personal Leaderboard) ─── */

type FollowsSortKey = "score" | "avgPnl" | "avgPnlPerDay" | "winRate" | "netPnlCaptured" | "totalCalls";
type SortDir = "asc" | "desc";

const FOLLOWS_SORT_STORAGE_KEY = "follows-leaderboard-sort";

function loadSortPrefs(): { key: FollowsSortKey; dir: SortDir } {
  try {
    const raw = localStorage.getItem(FOLLOWS_SORT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.key && parsed.dir) return parsed;
    }
  } catch {}
  return { key: "score", dir: "desc" };
}

const FollowsTab = ({ userId }: { userId: string }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPremium } = useUserRole();
  const { data: allInfluencers = [], isLoading: leaderboardLoading } = useLeaderboard();
  const [sortKey, setSortKey] = useState<FollowsSortKey>(() => loadSortPrefs().key);
  const [sortDir, setSortDir] = useState<SortDir>(() => loadSortPrefs().dir);
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subModalHandle, setSubModalHandle] = useState("");
  const { filters, updateFilter, clearFilters, hasAnyFilter: hasColumnFilters, qualifyingIds: globalIds, hasAnyFilter: hasGlobalFilter } = useGlobalInfluencerFilter();

  const { data: follows = [], isLoading: followsLoading } = useQuery({
    queryKey: ["dashboard-follows", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("follows")
        .select("x_user_id")
        .eq("user_id", userId);
      if (error) throw error;
      return data ?? [];
    },
  });

  const followedIds = useMemo(() => new Set(follows.map((f) => f.x_user_id)), [follows]);

  const handleFollowToggle = useCallback(async (xUserId: string, handle: string) => {
    if (!user) {
      window.location.href = `/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    if (!isPremium) {
      setSubModalHandle(handle);
      setSubModalOpen(true);
      return;
    }
    const isCurrentlyFollowing = followedIds.has(xUserId);
    if (isCurrentlyFollowing) {
      await supabase.from("follows").delete().eq("user_id", user.id).eq("x_user_id", xUserId);
      toast.success("Unfollowed");
    } else {
      await supabase.from("follows").insert({ user_id: user.id, x_user_id: xUserId });
      toast.success("Following!");
    }
  }, [user, isPremium, followedIds]);

  const handleSort = (key: FollowsSortKey) => {
    let newDir: SortDir;
    if (sortKey === key) {
      newDir = sortDir === "asc" ? "desc" : "asc";
    } else {
      newDir = "desc";
    }
    setSortKey(key);
    setSortDir(newDir);
    localStorage.setItem(FOLLOWS_SORT_STORAGE_KEY, JSON.stringify({ key, dir: newDir }));
  };

  const filtered = useMemo(() => {
    return allInfluencers
      .filter((i) => followedIds.has(i.id))
      .filter((i) => !hasGlobalFilter || globalIds.has(i.id))
      .filter((i) => filterInfluencer(i, filters))
      .sort((a, b) => {
        const valA = sortKey === "score" ? a.score : sortKey === "netPnlCaptured" ? a.netPnlCaptured : a[sortKey];
        const valB = sortKey === "score" ? b.score : sortKey === "netPnlCaptured" ? b.netPnlCaptured : b[sortKey];
        return sortDir === "asc" ? valA - valB : valB - valA;
      });
  }, [allInfluencers, followedIds, sortKey, sortDir, filters, hasColumnFilters, globalIds]);

  const isLoading = leaderboardLoading || followsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (follows.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            You're not following any influencers yet. Discover them on the leaderboard.
          </p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate("/leaderboard")}>
            Browse leaderboard
          </Button>
        </CardContent>
      </Card>
    );
  }

  const sortColumns: { key: FollowsSortKey; label: string }[] = [
    { key: "score", label: "Score" },
    { key: "avgPnl", label: "Avg PnL" },
    { key: "avgPnlPerDay", label: "PnL/day" },
    { key: "winRate", label: "Win Rate" },
    { key: "netPnlCaptured", label: "Net PnL" },
    { key: "totalCalls", label: "Calls / Pos" },
  ];

  return (
    <>
      <div className="bg-card border border-border rounded-xl overflow-x-auto">
        {filtered.length > 0 && (
          <>
          <div className="hidden md:grid grid-cols-[60px_minmax(160px,1fr)_100px_100px_100px_100px_110px_140px_40px] items-center gap-4 px-5 py-3 border-b border-border">
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">Rank</span>
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">Influencer</span>
            {sortColumns.map((col) => (
              <button
                key={col.key}
                onClick={() => handleSort(col.key)}
                className={`font-mono text-[10px] uppercase tracking-widest text-center flex items-center justify-center gap-1 hover:text-foreground transition-colors ${
                  sortKey === col.key ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {col.label}
                {sortKey === col.key
                  ? sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                  : <ArrowUpDown className="w-3 h-3 opacity-40" />}
              </button>
            ))}
            <span></span>
          </div>
          <LeaderboardFilterRow
            filters={filters}
            updateFilter={updateFilter}
            clearFilters={clearFilters}
            hasAnyFilter={hasColumnFilters}
          />
          </>
        )}
        {filtered.map((influencer, i) => (
          <div key={influencer.id} className="relative [&:has(button:hover)]:z-10">
            <InfluencerCard
              influencer={influencer}
              rank={i + 1}
              onClick={() => navigate(`/profile/${influencer.handle.replace(/^@/, "")}`)}
              followState={{
                isFollowing: followedIds.has(influencer.id),
                onToggle: () => handleFollowToggle(influencer.id, influencer.handle),
              }}
            />
          </div>
        ))}
        {filtered.length === 0 && follows.length > 0 && (
          <div className="text-center py-12 text-muted-foreground font-mono text-sm">
            None of the influencers you follow have been analyzed yet.
          </div>
        )}
      </div>
      <FollowSubscriptionModal
        open={subModalOpen}
        onOpenChange={setSubModalOpen}
        handle={subModalHandle}
      />
    </>
  );
};

/* ─── Main Dashboard ─── */

const DashboardPage = () => {
  const { user, session, loading } = useAuth();
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get("tab");
  const validTabs = ["positions", "follows", "watchlist", ...(isAdmin ? ["st-positions", "st-orders"] : [])];
  const activeTab = validTabs.includes(tabFromUrl || "") ? tabFromUrl! : "follows";

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  if (loading) return <div className="min-h-screen bg-background" />;

  if (!session) {
    const LandingPage = lazy(() => import("./LandingPage"));
    return (
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <LandingPage />
      </Suspense>
    );
  }

  return (
    <main className="flex-1 p-4 md:p-6 w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Your positions, watchlist, and followed influencers.
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} title="Settings">
          <Settings className="w-5 h-5" />
        </Button>
      </div>


      <ActiveFilterBadge />

      {/* Tabbed content */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-full justify-start bg-muted/50">
          <TabsTrigger value="follows" className="gap-1.5">
            <Users className="w-3.5 h-3.5" /> Following
          </TabsTrigger>
          <TabsTrigger value="positions" className="gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Positions
          </TabsTrigger>
           <TabsTrigger value="watchlist" className="gap-1.5">
             <Eye className="w-3.5 h-3.5" /> Watchlist
           </TabsTrigger>
           {isAdmin && (
             <>
               <TabsTrigger value="st-positions" className="gap-1.5">
                 <Crosshair className="w-3.5 h-3.5" /> ST Positions
               </TabsTrigger>
               <TabsTrigger value="st-orders" className="gap-1.5">
                 <ClipboardList className="w-3.5 h-3.5" /> ST Orders
               </TabsTrigger>
             </>
           )}
         </TabsList>

        <TabsContent value="follows" className="mt-4">
          <FollowsTab userId={user!.id} />
        </TabsContent>

        <TabsContent value="positions" className="mt-4">
          <PositionsTab userId={user!.id} />
        </TabsContent>

        <TabsContent value="watchlist" className="mt-4">
           <WatchlistTab />
         </TabsContent>

         {isAdmin && (
           <>
             <TabsContent value="st-positions" className="mt-4">
               <SnapTradePositionsTab />
             </TabsContent>
             <TabsContent value="st-orders" className="mt-4">
               <SnapTradeOrdersTab />
             </TabsContent>
           </>
         )}
      </Tabs>
    </main>
  );
};

export default DashboardPage;
